import React, { useMemo, useState, useEffect } from 'react'
import { connect, useSelector } from 'react-redux'
import { TextInput, Pane, Text, List, Flex, Button, Dialog, Toast } from '@leverege/ui-elements'
import classnames from 'classnames'

import EnumCard from './EnumCard'
import Editor from '../../editors/enums'
import EnumDialog from '../../editors/enums/EnumTypeCreateDialog'

import S from './EnumGroupScreen.css'

function filterEnums( filter, enumList = [] ) {
  if ( !filter ) return enumList
  const term = filter?.toLowerCase()
  const searchFields = [ 'name', 'type', 'description' ]
  return enumList.filter( item => searchFields.some( field => item[field]?.toLowerCase()?.includes( term ) ) )
}

function EnumGroupScreen( props ) {
  const { actions, dispatch, itemActions, onSelect, get, canCreate = true } = props
  const [ filter, setFilter ] = useState()
  const [ active, setActive ] = useState()
  const [ toDelete, setToDelete ] = useState()
  const [ showCreate, setShowCreate ] = useState( false )
  const items = useSelector( state => get( state ) )
  const loadData = () => { dispatch( actions.list() ) }
  useEffect( () => loadData(), [] )

  const onUpdateFilter = ( { value } ) => {
    setActive()
    setFilter( value )
  }

  const onDelete = ( { data } ) => setToDelete( data )

  const onConfirmDelete = async () => {
    try {
      if ( active?.value?.type === toDelete ) {
        setActive()
      }
      await dispatch( itemActions( toDelete ).remove() )
      Toast.success( `Enum ${toDelete} Successfully Removed`, {} )
    } catch ( err ) {
      Toast.error( [ `Failed to remove enum ${toDelete}`, err.message ], {} )
    }
    setToDelete( null )
  }

  const createEnum = async ( { value : { name, type, description } } ) => {
    try {
      await dispatch( actions.create( type, { name, description } ) )
      Toast.success( `Enum ${type} - ${name} Created Successfully`, {} )
    } catch ( err ) {
      Toast.error( [ 'Failed to create enum', err.message ], {} )
      console.error( err )
    }
    setShowCreate( false )
  }

  const getValues = async enumType => dispatch( itemActions( enumType ).values() )

  const enumList = useMemo( () => Object.values( items ), [ items ] )
  const filteredList = useMemo( () => filterEnums( filter, enumList ), [ filter, enumList ] )

  const itemProps = {
    ...props,
    active,
    enumType : 'enum',
    onSelect,
    getValues,
    onDelete,
    onClickCard : item => setActive( active?.value?.type === item?.value?.type ? null : item )
  }

  const factory = { create : e => <EnumCard key={e?.type} item={e} {...itemProps} /> } // eslint-disable-line react/destructuring-assignment
  const editor = Editor.createEditor( { enumType : active?.value?.type, type : 'enum', itemActions } )
  return (
    <Pane className={classnames( S.screen, { [S.active] : active } )}>
      <Pane className={S.cardSection}>
        <Flex className={S.actionHeader} justify="space-between">
          <Text variant="screenTitle">
            {`${window.location.pathname.includes( 'resourceEnum' ) ? 'Resource ' : ''}Enums`}
          </Text>
          <Flex className={S.title} justify="flex-end">
            <TextInput
              value={filter}
              hint="Search enums"
              className={S.search}
              changeOnBlur={false}
              changeOnReturn
              onChange={onUpdateFilter}/>
            { canCreate && (
              <Button
                className={S.new}
                variant="add"
                icon="fa fa-fw fa-circle-plus fa-lg"
                onClick={() => setShowCreate( true )}>
                New Enum
              </Button>
            )}
          </Flex>
        </Flex>
        <List.Dynamic items={filteredList} factory={factory} />
        <NotFound filter={filter} filteredList={filteredList} />
      </Pane>
      {active && editor}
      <Dialog.Question
        variant="dialogMedium"
        show={toDelete}
        title={`Delete the ${toDelete} enum? All values and metadata for ${toDelete} will be lost.`} // eslint-disable-line max-len
        onOkay={onConfirmDelete}
        onCancel={() => setToDelete( null )}
        eventData="Delete"
        okay={`Delete ${toDelete}`}
        okayVariant="primaryDestructive" />
      <EnumDialog
        show={showCreate}
        onSubmit={createEnum}
        eventData="Create"
        onClose={() => setShowCreate( false )} />
    </Pane>
  )
}

function NotFound( { filter, filteredList } ) {
  return !filteredList.length && (
    <Flex justify="center" style={{ width : '100%' }}><Text>{`No ${filter ? 'Matching ' : ''}Items`}</Text></Flex>
  )
}

export default connect()( EnumGroupScreen )
